// src/components/Layout/Navbar.jsx
import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { signOutUser } from "../../services/auth";
import LoginModal from "../Auth/LoginModal";
import SignupModal from "../Auth/SignupModal";

const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showSignupModal, setShowSignupModal] = useState(false);
  const [showCreateDropdown, setShowCreateDropdown] = useState(false);
  const [showMobileCreateMenu, setShowMobileCreateMenu] = useState(false);
  const { currentUser } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await signOutUser();
    // Redirect to home page instead of recruiter page
    navigate('/');
  };

  // Check if user is on recruiter dashboard
  const isRecruiterDashboard = location.pathname.includes('/recruiter-dashboard') || 
                              location.pathname.includes('/recruiter-pending') ||
                              location.pathname.includes('/recruiter-rejected');

  // Main navigation links (without profile)
  const mainNavLinks = [
    { path: "/", name: "Home", icon: "fas fa-home" },
    { path: "/jobs", name: "Jobs", icon: "fas fa-briefcase" },
  ];

  // For recruiter dashboard - show NOTHING (just logo and logout)
  const getNavLinks = () => {
    if (isRecruiterDashboard) {
      return []; // No links on recruiter dashboard
    }
    return mainNavLinks;
  };

  const navLinks = getNavLinks();

  // Create dropdown items
  const createItems = [
    { path: "/cv-creator", name: "CV Creator", icon: "fas fa-file-alt" },
    { path: "/cover-letter", name: "Cover Letter", icon: "fas fa-envelope" },
  ];

  // Mobile create menu items
  const mobileCreateItems = [
    { path: "/cv-creator", name: "CV Creator", icon: "fas fa-file-alt" },
    { path: "/cover-letter", name: "Cover Letter", icon: "fas fa-envelope" },
  ];

  return (
    <>
      <nav className="bg-gradient-to-r from-gradpath-green to-gradpath-dark shadow-lg sticky top-0 z-50">
        <div className="container mx-auto px-4 py-3 flex justify-between items-center">
          {/* Logo */}
          <Link
            to="/"
            className="flex items-center gap-2 text-white text-xl font-bold"
          >
            <i className="fas fa-graduation-cap text-2xl"></i>
            <span>GradPath</span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-4">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`text-white hover:bg-white/20 px-3 py-2 rounded-lg transition ${
                  location.pathname === link.path ? "bg-white/20" : ""
                }`}
              >
                <i className={`${link.icon} mr-2`}></i>
                {link.name}
              </Link>
            ))}

            {/* Only show Create, Hire Talent, Auth if NOT on recruiter dashboard */}
            {!isRecruiterDashboard && (
              <>
                {/* Create Dropdown */}
                <div 
                  className="relative"
                  onMouseEnter={() => setShowCreateDropdown(true)}
                  onMouseLeave={() => setShowCreateDropdown(false)}
                >
                  <button
                    className="text-white hover:bg-white/20 px-3 py-2 rounded-lg transition flex items-center gap-2"
                  >
                    <i className="fas fa-plus-circle"></i>
                    Create
                    <i className="fas fa-chevron-down text-xs"></i>
                  </button>
                  {showCreateDropdown && (
                    <div className="absolute top-full left-0 mt-1 bg-white rounded-lg shadow-lg py-2 min-w-[200px] z-50">
                      {createItems.map((item) => (
                        <Link
                          key={item.path}
                          to={item.path}
                          className="flex items-center gap-3 px-4 py-2 text-gray-700 hover:bg-gray-50 transition"
                          onClick={() => setShowCreateDropdown(false)}
                        >
                          <i className={`${item.icon} text-gradpath-green w-5`}></i>
                          {item.name}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>

                {/* Profile - Show after Create when logged in */}
                {currentUser && (
                  <Link
                    to="/profile"
                    className={`text-white hover:bg-white/20 px-3 py-2 rounded-lg transition ${
                      location.pathname === "/profile" ? "bg-white/20" : ""
                    }`}
                  >
                    <i className="fas fa-user mr-2"></i>
                    Profile
                  </Link>
                )}

                {/* Hire Talent - Only when logged out */}
                {!currentUser && (
                  <Link
                    to="/for-recruiters"
                    className="text-white hover:bg-white/20 px-3 py-2 rounded-lg transition"
                  >
                    <i className="fas fa-user-tie mr-2"></i>
                    Hire Talent
                  </Link>
                )}

                {/* Auth Buttons */}
                {currentUser ? (
                  <button
                    onClick={handleLogout}
                    className="text-white hover:bg-white/20 px-3 py-2 rounded-lg transition"
                  >
                    <i className="fas fa-sign-out-alt mr-2"></i>Logout
                  </button>
                ) : (
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setShowLoginModal(true)}
                      className="text-white hover:bg-white/20 px-3 py-2 rounded-lg transition"
                    >
                      <i className="fas fa-sign-in-alt mr-2"></i>Sign In
                    </button>
                    <button
                      onClick={() => setShowSignupModal(true)}
                      className="text-white hover:bg-white/20 px-3 py-2 rounded-lg transition"
                    >
                      <i className="fas fa-user-plus mr-2"></i>Sign Up
                    </button>
                  </div>
                )}
              </>
            )}

            {/* On recruiter dashboard - only show logout */}
            {isRecruiterDashboard && currentUser && (
              <button
                onClick={handleLogout}
                className="text-white hover:bg-white/20 px-3 py-2 rounded-lg transition"
              >
                <i className="fas fa-sign-out-alt mr-2"></i>Logout
              </button>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden text-white text-2xl"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            <i className={`fas ${isMenuOpen ? "fa-times" : "fa-bars"}`}></i>
          </button>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="md:hidden bg-gradient-to-r from-gradpath-green to-gradpath-dark px-4 pb-4">
            {/* On recruiter dashboard - show nothing */}
            {!isRecruiterDashboard ? (
              <>
                {/* Main Links */}
                {mainNavLinks.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    className="block text-white py-2"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <i className={`${link.icon} mr-2 w-6`}></i>
                    {link.name}
                  </Link>
                ))}

                {/* Create Section - Mobile */}
                <div className="border-t border-white/20 mt-2 pt-2">
                  <button
                    onClick={() => setShowMobileCreateMenu(!showMobileCreateMenu)}
                    className="flex items-center justify-between w-full text-white py-2"
                  >
                    <span>
                      <i className="fas fa-plus-circle mr-2 w-6"></i>
                      Create
                    </span>
                    <i className={`fas fa-chevron-${showMobileCreateMenu ? 'up' : 'down'} text-xs`}></i>
                  </button>
                  {showMobileCreateMenu && (
                    <div className="ml-6 space-y-1">
                      {mobileCreateItems.map((item) => (
                        <Link
                          key={item.path}
                          to={item.path}
                          className="block text-white/80 hover:text-white py-2 pl-2"
                          onClick={() => {
                            setIsMenuOpen(false);
                            setShowMobileCreateMenu(false);
                          }}
                        >
                          <i className={`${item.icon} mr-2 w-6`}></i>
                          {item.name}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>

                {/* Profile - Show after Create when logged in */}
                {currentUser && (
                  <Link
                    to="/profile"
                    className="block text-white py-2"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <i className="fas fa-user mr-2 w-6"></i>
                    Profile
                  </Link>
                )}

                {/* Hire Talent - Only when logged out */}
                {!currentUser && (
                  <Link
                    to="/for-recruiters"
                    className="block text-white py-2"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <i className="fas fa-user-tie mr-2 w-6"></i>
                    Hire Talent
                  </Link>
                )}

                {/* Auth */}
                {currentUser ? (
                  <button
                    onClick={handleLogout}
                    className="block text-white py-2 w-full text-left"
                  >
                    <i className="fas fa-sign-out-alt mr-2 w-6"></i>Logout
                  </button>
                ) : (
                  <>
                    <button
                      onClick={() => {
                        setShowLoginModal(true);
                        setIsMenuOpen(false);
                      }}
                      className="block text-white py-2 w-full text-left"
                    >
                      <i className="fas fa-sign-in-alt mr-2 w-6"></i>Sign In
                    </button>
                    <button
                      onClick={() => {
                        setShowSignupModal(true);
                        setIsMenuOpen(false);
                      }}
                      className="block text-white py-2 w-full text-left"
                    >
                      <i className="fas fa-user-plus mr-2 w-6"></i>Sign Up
                    </button>
                  </>
                )}
              </>
            ) : (
              // Recruiter Dashboard Mobile Menu
              currentUser && (
                <button
                  onClick={handleLogout}
                  className="block text-white py-2 w-full text-left"
                >
                  <i className="fas fa-sign-out-alt mr-2 w-6"></i>Logout
                </button>
              )
            )}
          </div>
        )}
      </nav>

      {/* Modals */}
      <LoginModal
        isOpen={showLoginModal}
        onClose={() => setShowLoginModal(false)}
        onSwitchToSignup={() => {
          setShowLoginModal(false);
          setShowSignupModal(true);
        }}
      />
      <SignupModal
        isOpen={showSignupModal}
        onClose={() => setShowSignupModal(false)}
        onSwitchToLogin={() => {
          setShowSignupModal(false);
          setShowLoginModal(true);
        }}
      />
    </>
  );
};

export default Navbar;